import type { StreamPlatform } from '@/models/eventCourtStreamLinkModel';

/** Absolute public web URLs only; credentials and control characters are never public links. */
export function safeWatchUrl(raw: string | null): string | null {
  if (!raw || /[\u0000-\u001f\u007f]/.test(raw)) return null;
  try {
    const url = new URL(raw);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      !url.hostname ||
      url.username ||
      url.password
    )
      return null;
    return url.href;
  } catch {
    return null;
  }
}

export type StreamDisplay = 'inline' | 'link';
export type RecognisedStream = {
  /** Normalised https URL. */
  url: string;
  platform: StreamPlatform;
  display: StreamDisplay;
  /** Set iff display === 'inline'. */
  embedUrl: string | null;
  kind: 'broadcast' | 'channel' | 'handle' | 'page';
};

const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
const FACEBOOK_HOSTS = new Set(['facebook.com', 'www.facebook.com', 'm.facebook.com']);
const SQUASHTV_HOSTS = new Set(['squash.tv', 'www.squash.tv', 'squashtv.com', 'www.squashtv.com']);
const WORLDSQUASH_HOSTS = new Set(['worldsquash.tv', 'www.worldsquash.tv']);
const EMBED_HOSTS = new Set(['www.youtube.com', 'www.youtube-nocookie.com', 'www.facebook.com']);

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const CHANNEL_ID = /^UC[A-Za-z0-9_-]{22}$/;

const link = (
  url: string,
  platform: StreamPlatform,
  kind: RecognisedStream['kind'] = 'page',
): RecognisedStream => ({ url, platform, display: 'link', embedUrl: null, kind });

const youtubeVideo = (url: string, id: string): RecognisedStream => ({
  url,
  platform: 'youtube',
  display: 'inline',
  embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
  kind: 'broadcast',
});

function recogniseYoutube(url: string, u: URL): RecognisedStream {
  if (u.hostname === 'youtu.be') {
    const id = u.pathname.split('/')[1] ?? '';
    return VIDEO_ID.test(id) ? youtubeVideo(url, id) : link(url, 'youtube');
  }
  const parts = u.pathname.split('/').filter(Boolean);
  if (parts[0] === 'watch') {
    const id = u.searchParams.get('v') ?? '';
    return VIDEO_ID.test(id) ? youtubeVideo(url, id) : link(url, 'youtube');
  }
  if ((parts[0] === 'live' || parts[0] === 'embed') && VIDEO_ID.test(parts[1] ?? '')) {
    return youtubeVideo(url, parts[1]);
  }
  if (parts[0] === 'channel' && CHANNEL_ID.test(parts[1] ?? '')) {
    return {
      url,
      platform: 'youtube',
      display: 'inline',
      embedUrl: `https://www.youtube-nocookie.com/embed/live_stream?channel=${parts[1]}`,
      kind: 'channel',
    };
  }
  if (parts[0]?.startsWith('@')) return link(url, 'youtube', 'handle');
  return link(url, 'youtube');
}

function recogniseFacebook(url: string, u: URL): RecognisedStream {
  const isVideo =
    u.hostname === 'fb.watch' ||
    /\/(videos|watch|reel)(\/|$)/.test(u.pathname) ||
    u.pathname.startsWith('/video.php');
  if (!isVideo) return link(url, 'facebook');
  return {
    url,
    platform: 'facebook',
    display: 'inline',
    embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`,
    kind: 'broadcast',
  };
}

/** Recognise a pasted stream link. null = refuse (unsafe, not https, or not a URL). */
export function recogniseStreamUrl(raw: string): RecognisedStream | null {
  const safe = safeWatchUrl(raw.trim());
  if (!safe) return null;
  const u = new URL(safe);
  if (u.protocol !== 'https:') return null;
  const url = u.href;
  const host = u.hostname.toLowerCase();
  if (YOUTUBE_HOSTS.has(host) || host === 'youtu.be') return recogniseYoutube(url, u);
  if (FACEBOOK_HOSTS.has(host) || host === 'fb.watch') return recogniseFacebook(url, u);
  if (SQUASHTV_HOSTS.has(host)) return link(url, 'squashtv');
  if (WORLDSQUASH_HOSTS.has(host)) return link(url, 'worldsquash');
  return link(url, 'other');
}

/** Only these hosts are ever put in an iframe. */
export function embedHostAllowed(embedUrl: string): boolean {
  try {
    const u = new URL(embedUrl);
    return u.protocol === 'https:' && EMBED_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}
